import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useScopedTranslation } from 'app/hooks/i18n';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useAllInterests, useCreateOneInterest, useDeleteOneInterest } from 'app/network/interests';
import { interestKeys } from 'app/network/keys';
import { useAllTags, type Tag } from 'app/network/listing';
import type { Interest } from 'app/types/interest';
import type { NavigationScreen } from 'app/types/navigation';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { usePageTitle } from 'app/utils/web-helpers';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ActivityIndicator, Pressable, RefreshControl, StyleSheet, TextInput } from 'react-native';
import { useUser } from 'app/network/user';
import { createTheme as createDSTheme } from 'app/shopify';
import { useNavigationOptions } from 'app/hooks/navigation';
import { InterestTagChip } from './interest-tag-chip';
import { canUseInterests } from 'app/utils/helpers';
const theme = createDSTheme();

type ScopedT = ReturnType<typeof useScopedTranslation>['t'];

function getFilteredTags(allTags: Tag[] | undefined, searchQuery: string) {
  if (!searchQuery.trim()) return allTags ?? [];
  const normalizedQuery = searchQuery.toLowerCase();
  return (allTags ?? []).filter((tag) => tag.tagName.toLowerCase().includes(normalizedQuery));
}

function InterestsBody({
  inFlight,
  isLoading,
  noResults,
  searchQuery,
  selectedCount,
  selectedTags,
  t,
  toggleInterest,
  unselectedTags,
}: {
  inFlight: Set<string>;
  isLoading: boolean;
  noResults: boolean;
  searchQuery: string;
  selectedCount: number;
  selectedTags: Tag[];
  t: ScopedT;
  toggleInterest: (tagId: string) => void;
  unselectedTags: Tag[];
}) {
  if (isLoading) {
    return (
      <Column alignItems="center" marginTop="spacing.general.x12">
        <ActivityIndicator color={theme.colors['surface.tertiary']} size="large" />
      </Column>
    );
  }

  if (noResults) {
    return (
      <Column alignItems="center" marginTop="spacing.general.x8">
        <Text color="text.secondary" fontSize={15} textAlign="center">
          {t('noResults', { query: searchQuery })}
        </Text>
      </Column>
    );
  }

  return (
    <Column gap="spacing.general.x8">
      {selectedCount === 0 && !searchQuery ? (
        <Column
          alignItems="center"
          gap="spacing.general.x1"
          marginBottom="spacing.general.x2"
          padding="spacing.general.x4"
          style={styles.emptyCard}
        >
          <Text fontSize={24}>✨</Text>
          <Text fontSize={15} fontWeight="600" textAlign="center">
            {t('emptyTitle')}
          </Text>
          <Text color="text.secondary" fontSize={13} textAlign="center">
            {t('emptySubtitle')}
          </Text>
        </Column>
      ) : null}

      {selectedTags.length > 0 ? (
        <Column gap="spacing.general.x2">
          <Text color="text.secondary" fontSize={12} fontWeight="600" style={styles.sectionLabel}>
            {t('selectedSection', { count: selectedCount }).toUpperCase()}
          </Text>
          <Row style={styles.tagRow}>
            {selectedTags.map((tag) => (
              <InterestTagChip
                key={tag.id}
                isPending={inFlight.has(tag.id)}
                isSelected
                label={tag.tagName}
                onPress={() => toggleInterest(tag.id)}
              />
            ))}
          </Row>
        </Column>
      ) : null}

      {unselectedTags.length > 0 ? (
        <Column gap="spacing.general.x2">
          <Text color="text.secondary" fontSize={12} fontWeight="600" style={styles.sectionLabel}>
            {t('exploreSection').toUpperCase()}
          </Text>
          <Row style={styles.tagRow}>
            {unselectedTags.map((tag) => (
              <InterestTagChip
                key={tag.id}
                isPending={inFlight.has(tag.id)}
                isSelected={false}
                label={tag.tagName}
                onPress={() => toggleInterest(tag.id)}
              />
            ))}
          </Row>
        </Column>
      ) : null}
    </Column>
  );
}

const MyInterestsScreenContent: NavigationScreen<'my-interests'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: myInterests, refetch: refetchInterests, isRefetching } = useAllInterests();
  const { data: allTags, refetch: refetchTags } = useAllTags();
  const { data: user } = useUser();

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  // Optimistic cache updates: the UI reads from `myInterests` directly, so
  // patching the query cache in onMutate gives instant feedback. On error we
  // roll back to the snapshot taken before the mutation.
  const createInterest = useCreateOneInterest({
    onMutate: async ({ tagId, type, action }) => {
      await queryClient.cancelQueries({ queryKey: interestKeys.lists() });
      const previous = queryClient.getQueryData<Interest[]>(interestKeys.lists()) ?? [];
      const optimistic: Interest = {
        id: `optimistic-${tagId}`,
        userId: user?.id ?? '',
        tagId,
        type,
        action,
        tagName: allTags?.find((tt) => tt.id === tagId)?.tagName ?? '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      queryClient.setQueryData<Interest[]>(interestKeys.lists(), (old) => [...(old ?? []), optimistic]);
      return { previous, optimistic };
    },
    onError: (_err, _vars, context) => {
      const { optimistic } = (context as { optimistic?: Interest } | undefined) ?? {};
      if (optimistic) {
        queryClient.setQueryData<Interest[]>(interestKeys.lists(), (old) =>
          (old ?? []).filter((i) => i.id !== optimistic.id),
        );
      }
    },
  });

  const deleteInterest = useDeleteOneInterest({
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: interestKeys.lists() });
      const previous = queryClient.getQueryData<Interest[]>(interestKeys.lists()) ?? [];
      const deletedItem = previous.find((interest) => interest.id === id);
      queryClient.setQueryData<Interest[]>(interestKeys.lists(), (old) =>
        (old ?? []).filter((interest) => interest.id !== id),
      );
      return { previous, deletedItem };
    },
    onError: (_err, _id, context) => {
      const { deletedItem } = (context as { deletedItem?: Interest } | undefined) ?? {};
      if (deletedItem) {
        queryClient.setQueryData<Interest[]>(interestKeys.lists(), (old) => [...(old ?? []), deletedItem]);
      }
    },
  });

  const setPageTitle = usePageTitle('my-interests');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  // Map tagId → interest.id for current interests (server truth, with
  // optimistic patches applied by onMutate).
  const interestByTagId = (() => {
    const map = new Map<string, string>();
    myInterests?.forEach((i) => map.set(i.tagId, i.id));
    return map;
  })();

  // Per-tag in-flight set: prevents toggling a tag whose mutation is still
  // running (which would otherwise try to delete a not-yet-real optimistic id
  // or create a duplicate).
  const [inFlight, setInFlight] = useState<Set<string>>(new Set());

  const toggleInterest = (tagId: string) => {
    if (myInterests === undefined || allTags === undefined) return;
    if (inFlight.has(tagId)) return;

    const existingId = interestByTagId.get(tagId);

    setInFlight((prev) => {
      const next = new Set(prev);
      next.add(tagId);
      return next;
    });

    const settle = () => {
      setInFlight((prev) => {
        if (!prev.has(tagId)) return prev;
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });
    };

    const promise = existingId
      ? deleteInterest.mutateAsync(existingId)
      : createInterest.mutateAsync({ tagId, type: 'interest', action: 'borrow' });

    promise.catch(() => undefined).finally(settle);
  };

  const isTagSelected = (tagId: string) => interestByTagId.has(tagId);

  const filteredTags = getFilteredTags(allTags, searchQuery);

  const selectedTags = filteredTags.filter((tag) => isTagSelected(tag.id));

  const unselectedTags = filteredTags.filter((tag) => !isTagSelected(tag.id));

  const selectedCount = (allTags ?? []).filter((tag) => isTagSelected(tag.id)).length;

  const noResults = filteredTags.length === 0 && searchQuery.trim().length > 0;
  const isLoading = allTags === undefined || myInterests === undefined;

  return (
    <Page
      refreshControl={
        <RefreshControl
          onRefresh={() => {
            void refetchInterests();
            void refetchTags();
          }}
          refreshing={isRefetching}
        />
      }
      title={t('title')}
    >
      {/* Subtitle */}
      <Text color="text.secondary" fontSize={14} marginBottom="spacing.general.x4" variant="bodyBase">
        {t('subtitle')}
      </Text>

      {/* Search bar */}
      <Row alignItems="center" gap="spacing.general.x1" marginBottom="spacing.general.x8" style={styles.searchBar}>
        <Text fontSize={16}>🔍</Text>
        <TextInput
          onChangeText={setSearchQuery}
          placeholder={t('searchPlaceholder')}
          placeholderTextColor={theme.colors['text.secondary']}
          returnKeyType="search"
          style={styles.searchInput}
          value={searchQuery}
        />
        {searchQuery.length > 0 ? (
          <Pressable
            accessibilityHint={t('search.clearSearchHint', { ns: 'accessibility' })}
            accessibilityLabel={t('search.clearSearch', { ns: 'accessibility' })}
            accessibilityRole="button"
            hitSlop={8}
            onPress={() => setSearchQuery('')}
            style={styles.clearButton}
          >
            <Text color="text.secondary" fontSize={14}>
              ✕
            </Text>
          </Pressable>
        ) : null}
      </Row>

      <InterestsBody
        inFlight={inFlight}
        isLoading={isLoading}
        noResults={noResults}
        searchQuery={searchQuery}
        selectedCount={selectedCount}
        selectedTags={selectedTags}
        t={t}
        toggleInterest={toggleInterest}
        unselectedTags={unselectedTags}
      />
    </Page>
  );
};

const MyInterestsScreenComponent: NavigationScreen<'my-interests'> = (props) => {
  const interestsAvailable = canUseInterests();
  const { navigation } = props;
  useEffect(() => {
    if (!interestsAvailable) navigation.replace('profile-overview');
  }, [interestsAvailable, navigation]);

  return interestsAvailable ? <MyInterestsScreenContent {...props} /> : null;
};

export const MyInterestsScreen: NavigationScreen<'my-interests'> = withOptionalMetrics(
  'MyInterestsScreen',
  withErrorBoundary(withDashboardLayout(MyInterestsScreenComponent)),
);

const styles = StyleSheet.create({
  searchBar: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
    paddingHorizontal: theme.spacing['spacing.general.x3'],
    paddingVertical: theme.spacing['spacing.general.x1'],
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    paddingVertical: 4,
    color: '#161616',
  },
  clearButton: {
    padding: 4,
  },
  sectionLabel: {
    letterSpacing: 0.8,
  },
  tagRow: {
    flexWrap: 'wrap',
    gap: 6,
  },
  emptyCard: {
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
  },
});
